import { buildDeviceBoxLookup } from "./deviceBox";
import type { AssignableDevice, BuildAssignableDevices } from "./types";

export const buildAssignableDevices: BuildAssignableDevices = ({
  geometry,
  unfedDevices,
  unrouted,
  deviceTypeByElementId = {},
  isOnBoard,
  isInBoardArea,
  acceptsCategory,
  elementBoxes = [],
}) => {
  const devices: AssignableDevice[] = [];
  const boxOf = buildDeviceBoxLookup(elementBoxes);
  const typeOf = (elementId: string | undefined) =>
    elementId ? deviceTypeByElementId[elementId] : undefined;

  Object.entries(geometry?.byCircuit ?? {}).forEach(([circuitId, state]) => {
    if (isOnBoard && !isOnBoard(circuitId)) return;
    state.paths.forEach((path) => {
      const position = path.waypoints[path.waypoints.length - 1];
      if (!position) return;
      const type = typeOf(path.elementId);
      devices.push({
        kind: "onCircuit",
        key: `d:${path.destinationId}`,
        destinationId: path.destinationId,
        circuitId,
        position,
        bbox: boxOf(path.elementId, position),
        name: type?.name ?? path.destinationName,
        category: type?.category ?? "other",
      });
    });
  });

  const inArea = (point: { x: number; y: number }) => !isInBoardArea || isInBoardArea(point);
  const accepted = (category: AssignableDevice["category"]) => !acceptsCategory || acceptsCategory(category);
  unfedDevices.forEach((device) => {
    if (!inArea(device.position) || !accepted(device.category)) return;
    devices.push({
      kind: "unfed",
      key: `f:${device.destinationId}`,
      destinationId: device.destinationId,
      position: device.position,
      bbox: boxOf(device.elementId, device.position),
      name: device.name,
      category: device.category,
    });
  });
  // Unfed devices are listed above under their own address; here only the ones on no run at all.
  unrouted.forEach((device) => {
    if (device.reason === "no_feeder" || !device.position || !inArea(device.position)) return;
    const type = typeOf(device.id);
    if (!accepted(type?.category ?? "other")) return;
    devices.push({
      kind: "noCircuit",
      key: `e:${device.id}`,
      elementId: device.id,
      position: device.position,
      bbox: boxOf(device.id, device.position),
      name: type?.name ?? device.name,
      category: type?.category ?? "other",
    });
  });
  return devices;
};
